import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { usersApi, type OrgUserRow } from '../features/users/api.ts';

const OFFICE_URL = 'https://office.wapplify.app';

function initials(name: string): string {
  return name.trim().slice(0, 2).toUpperCase();
}

function RoleBadge({ role }: { role: OrgUserRow['role'] }): JSX.Element {
  const label = role === 'SYS_ADMIN' ? 'System admin' : role === 'ORG_ADMIN' ? 'Admin' : 'Member';
  const cls = role === 'USER' ? 'pill pill-neutral' : 'pill pill-active';
  return <span className={cls}>{label}</span>;
}

export function SettingsUsers(): JSX.Element {
  const { data: users } = useQuery({ queryKey: ['team', 'users'], queryFn: () => usersApi.list().then((r) => r.users) });

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-2xl bg-[hsl(var(--tertiary-container))] p-4 text-[hsl(var(--on-tertiary-container))]">
        <Icon name="info" size={20} />
        <div className="min-w-0 text-sm">
          <p>
            Users are managed in <span className="font-medium">Wapplify Office</span>.
          </p>
          <a
            href={OFFICE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost btn-sm mt-1 -ml-2 inline-flex items-center gap-1"
          >
            Open Wapplify Office <Icon name="open_in_new" size={14} />
          </a>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Members</h2>
        <ul className="rounded-3xl bg-[hsl(var(--chrome))] overflow-hidden">
          {users?.map((u) => (
            <li
              key={u.id}
              className="flex items-center gap-3.5 border-b border-black/[0.05] px-4 py-3.5 last:border-b-0 sm:px-5"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-sm font-medium text-[hsl(var(--on-primary-container))]">
                {initials(u.name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[0.95rem] font-medium text-ink">{u.name}</div>
                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <RoleBadge role={u.role} />
                <span className={`pill ${u.isActive ? 'pill-success' : 'pill-neutral'}`}>
                  {u.isActive ? 'Active' : 'Disabled'}
                </span>
              </div>
            </li>
          ))}
          {users && users.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">No members yet.</li>
          )}
        </ul>
      </section>
    </div>
  );
}
